import {
  Box,
  Card,
  Container,
  Grid,
  Stack,
  Typography,
  useTheme,
  TextField,
  Button,
  AlertTitle,
  Alert,
  Toolbar,
  Tabs,
  Tab,
} from "@mui/material";
import React, { ReactNode, useState, useContext } from "react";
import {
  formatAddressLine1,
  formatAddressLine2,
  formatLicensePlateString,
  formatPhoneNumber,
  formatVehicleString,
  getCalcTitleFragments,
} from "utils";
import { useTranslation } from "react-i18next";
import { Logo, Contact, Vehicle, Status } from "types";
import { AppContext } from "context/appContext";
import cloudflareMock from "../../assets/cloudflare_mock.png";
import {
  AUTURA_ASPHALT_DARKER,
  AUTURA_ASPHALT_LIGHTER,
} from "context/themeContext";
interface FMCPreviewProps {
  logo: Logo;
  publicContactInfo: Contact;
  entityName: string;
}

export const FMCPreview = ({
  logo,
  publicContactInfo,
  entityName,
}: FMCPreviewProps): ReactNode => {
  const { palette } = useTheme();
  const { t } = useTranslation();
  const [tabValue, setTabValue] = useState<"makeModel" | "plate" | "vin">(
    "plate",
  );
  const { state } = useContext(AppContext);

  const address = publicContactInfo.location;

  const handleTabChange = (
    event: React.SyntheticEvent,
    newValue: "makeModel" | "plate" | "vin",
  ) => {
    setTabValue(newValue);
  };

  const vehicle: Vehicle = {
    year: "",
    make: "HONDA",
    model: "CIVIC",
    color: "RED",
    plate: {
      number: "TRAXERO",
      state: "Indiana",
    },
    vin: "TESTVIN1234567890",
    towerContactInfo: {
      name: state.tenant.name,
      status: Status.Active,
      tenantID: state.tenant.id,
      id: 0,
      location: state.tenant?.publicContactInfo?.location || {
        street: "123 Impound Lane",
        city: "Impound City",
        stateCode: "IN",
        postalCode: "46322",
      },
      primaryEmail: "test@test.com",
      primaryPhone: "234567890",
      website: "https://www.test.com",
    },
  };

  const findMyCarMsg = state.tenant.data?.findMyCarMsg;

  return (
    <Container component="aside" maxWidth="xs" sx={{ pb: "50px" }}>
      <Card
        sx={{
          borderRadius: 0,
          display: "flex",
          flexDirection: "column",
          backgroundColor: palette.primary.main,
        }}
        elevation={3}
        raised
        component={"header"}
      >
        {logo.url && (
          <img
            src={logo.url}
            style={{
              objectFit: "contain",
              padding: logo.padding || 0,
            }}
            height={120}
            alt={"logo banner"}
            id={"logo-banner"}
          />
        )}
        {!logo.url && (
          <Stack
            direction={"column"}
            spacing={0}
            id={"logo-banner"}
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: 120,
              width: "100%",
              padding: "0 16px",
            }}
          >
            {getCalcTitleFragments(entityName).map((piece, index) => (
              <Typography
                variant={"h4"}
                color={palette.primary.contrastText}
                noWrap
                key={index}
              >
                {piece}
              </Typography>
            ))}
          </Stack>
        )}
        <Stack
          direction={"column"}
          spacing={0}
          alignItems="center"
          sx={{ width: "100%", justifyContent: "center", pb: 2 }}
        >
          <Typography
            variant={"body2"}
            fontWeight={"medium"}
            color={palette.primary.contrastText}
          >
            {formatAddressLine1(address)}
          </Typography>
          <Typography
            variant={"body2"}
            fontWeight={"medium"}
            color={palette.primary.contrastText}
          >
            {formatAddressLine2(address)}
          </Typography>
        </Stack>
      </Card>
      <Card
        elevation={3}
        component={"main"}
        sx={{
          borderRadius: "0px",
          border: { xs: "none", sm: `1px solid ${AUTURA_ASPHALT_LIGHTER}` },
          background: "#FFF",
          position: "relative",
          minHeight: { xs: 490, sm: "auto" },
        }}
      >
        <Toolbar
          sx={{
            backgroundColor: AUTURA_ASPHALT_LIGHTER,
            textColor: palette.primary.contrastText,
            indicatorColor: palette.primary.main,
            color: palette.primary.contrastText,
            alignItems: "end",
            pb: 0.5,
          }}
        >
          <Tabs
            orientation="horizontal"
            value={tabValue}
            onChange={handleTabChange}
            indicatorColor={"primary"}
            textColor={"primary"}
            sx={{
              backgroundColor: AUTURA_ASPHALT_LIGHTER,
              height: "100%",
              width: "100%",
              display: "flex",
              justifyContent: "space-around",
              color: palette.primary.contrastText,
              alignItems: "center",
            }}
          >
            <Tab
              label={`${t("preview.fmc.make_model", { ns: "properties" })}`}
              value="makeModel"
              sx={{
                color: AUTURA_ASPHALT_DARKER,
                flex: 1,
                width: "100%",
              }}
            />
            <Tab
              label={t("preview.fmc.plate", { ns: "properties" })}
              value="plate"
              sx={{
                color: AUTURA_ASPHALT_DARKER,
                flex: 1,
                width: "100%",
              }}
            />
            <Tab
              label={t("preview.fmc.vin", { ns: "properties" })}
              value="vin"
              sx={{
                color: AUTURA_ASPHALT_DARKER,
                flex: 1,
                width: "100%",
              }}
            />
          </Tabs>
        </Toolbar>
        <Box sx={{ p: 2, position: "relative" }}>
          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: "transparent",
              zIndex: 2000,
            }}
            onClick={(e) => e.stopPropagation()}
          />
          <Grid container width={"100%"} spacing={3} sx={{ py: 4, px: 1 }}>
            {tabValue === "makeModel" && (
              <React.Fragment>
                <Grid size={12}>
                  <Stack direction={"column"} spacing={2}>
                    <TextField
                      label={`${t("vehicle.make", { ns: "entities" })}`}
                      margin={"none"}
                      variant={"outlined"}
                      value={vehicle.make}
                      name={"make"}
                      fullWidth
                    />
                    <TextField
                      label={`${t("vehicle.model", { ns: "entities" })}`}
                      margin={"none"}
                      variant={"outlined"}
                      value={vehicle.model}
                      name={"model"}
                      fullWidth
                    />
                  </Stack>
                </Grid>
              </React.Fragment>
            )}
            {tabValue === "plate" && (
              <React.Fragment>
                <Grid size={12}>
                  <Stack direction={"column"} spacing={2}>
                    <TextField
                      label={`${t("vehicle.plate.number", { ns: "entities" })}`}
                      margin={"none"}
                      variant={"outlined"}
                      value={vehicle.plate.number}
                      name={"plateNumber"}
                      fullWidth
                    />
                    <TextField
                      label={`${t("vehicle.plate.state", { ns: "entities" })}`}
                      margin={"none"}
                      variant={"outlined"}
                      value={vehicle.plate.state}
                      name={"plateState"}
                      fullWidth
                    />
                  </Stack>
                </Grid>
              </React.Fragment>
            )}
            {tabValue === "vin" && (
              <Grid size={12}>
                <TextField
                  variant="outlined"
                  margin="none"
                  fullWidth
                  name="vin"
                  label={t("vehicle.vin", { ns: "entities" })}
                  value={vehicle.vin}
                />
              </Grid>
            )}
            <Grid size={12}>
              <Alert severity={"success"} onClose={() => ({})}>
                <AlertTitle>
                  {t("preview.fmc.alert_title", { ns: "properties" })}
                </AlertTitle>
                <Stack direction="column" pt={2} spacing={2}>
                  <Stack direction={"row"}>
                    <Typography variant="body1" fontWeight="medium">
                      {formatVehicleString(vehicle)} -{" "}
                      {formatLicensePlateString(vehicle.plate)}
                    </Typography>
                  </Stack>
                  <Stack direction="column" justifyContent="space-between">
                    <Typography variant="body2">
                      {t("preview.fmc.yourCarGotTowed", { ns: "properties" })}
                    </Typography>
                    <Typography variant="body1" fontWeight="bold">
                      {state.tenant.name}
                    </Typography>
                  </Stack>
                  <Stack direction="column">
                    <Typography variant="body1" width="100%">
                      {formatAddressLine1(vehicle.towerContactInfo?.location)}
                    </Typography>
                    <Typography variant="body1" width="100%">
                      {formatAddressLine2(vehicle.towerContactInfo?.location)}
                    </Typography>
                    <Typography variant="body1" width="100%">
                      {vehicle.towerContactInfo?.primaryEmail}
                    </Typography>
                    <Typography variant="body1" width="100%">
                      {formatPhoneNumber(
                        vehicle.towerContactInfo?.primaryPhone,
                      )}
                    </Typography>
                  </Stack>
                  {vehicle.towerContactInfo?.website && (
                    <Typography variant="body2" fontWeight="bold" color="blue">
                      <a
                        href={vehicle.towerContactInfo.website}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {vehicle.towerContactInfo.website}
                      </a>
                    </Typography>
                  )}
                  {findMyCarMsg && (
                    <Typography variant="body2">{findMyCarMsg}</Typography>
                  )}
                </Stack>
              </Alert>
            </Grid>
            <Grid size={12}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "flex-end",
                  objectFit: "contain",
                  height: 68,
                }}
              >
                <img
                  src={cloudflareMock}
                  alt="mock cloudflare turnstile"
                  height={68}
                  width={"100%"}
                />
              </Box>
            </Grid>
            <Grid size={12}>
              <Stack direction="row" justifyContent="flex-end">
                <Button
                  variant="contained"
                  sx={{ height: 40, borderRadius: "100px", width: 150 }}
                >
                  <Typography
                    variant="button"
                    textTransform={"capitalize"}
                    color={palette.primary.contrastText}
                  >
                    {t("preview.fmc.button", { ns: "properties" })}
                  </Typography>
                </Button>
              </Stack>
            </Grid>
          </Grid>
        </Box>
      </Card>
      <Box sx={{ height: 50, width: "100%" }} />
    </Container>
  );
};
